import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "config/firebase";
import {
  modalVariants,
  childVariants,
  childVariantsDown,
  cld,
} from "shared/utils";
import "../styles/EditCertificatesModal.css";
const resolveImg = (val, width) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return cld(val, width);
};
const EditProjectModal = ({
  isOpen,
  onClose,
  projectData,
  onSave,
  isNewProject,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    image: "",
    title: "",
    category: "",
    projectDescription: "",
    skills: [],
    keyLearnings: [],
    codelink: "",
    demolink: "",
  });
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Temporary state for adding new skills and key learnings
  const [newSkillText, setNewSkillText] = useState("");
  const [newKeyLearningText, setNewKeyLearningText] = useState("");
  const [editingSkillIndex, setEditingSkillIndex] = useState(null);
  const [editingLearningIndex, setEditingLearningIndex] = useState(null);
  const [isAddingSkill, setIsAddingSkill] = useState(false);
  const [isAddingKeyLearning, setIsAddingKeyLearning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isNewProject || !projectData) {
        setFormData({
          id: "",
          image: "",
          title: "",
          category: "",
          projectDescription: "",
          skills: [],
          keyLearnings: [],
          codelink: "",
          demolink: "",
        });
        setNewSkillText("");
        setNewKeyLearningText("");
        setEditingSkillIndex(null);
        setEditingLearningIndex(null);
        setIsAddingSkill(false);
        setIsAddingKeyLearning(false);
      } else {
        setFormData({
          ...projectData,
          projectDescription: projectData.description || "",
        });
        setNewSkillText("");
        setNewKeyLearningText("");
        setEditingSkillIndex(null);
        setEditingLearningIndex(null);
        setIsAddingSkill(false);
        setIsAddingKeyLearning(false);
      }
      setErrors({});
      setImageFile(null);
    }
  }, [isOpen, projectData, isNewProject]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.category.trim()) newErrors.category = "Category is required";
    if (!formData.projectDescription.trim())
      newErrors.projectDescription = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setUploading(true);
    try {
      let imageUrl = formData.image;

      // Upload project image if new file selected
      if (imageFile) {
        const imageRef = ref(
          storage,
          `portfolio/Projects/${Date.now()}_${imageFile.name}`,
        );
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Convert skills and key learnings arrays
      const { projectDescription, ...rest } = formData;
      const projectToSave = {
        ...rest,
        id: formData.id || Date.now(),
        image: imageUrl,
        description: projectDescription,
        skills: formData.skills,
        keyLearnings: formData.keyLearnings,
      };

      onSave(projectToSave);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, image: previewUrl });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  // Skills handlers
  const handleAddSkill = () => {
    if (newSkillText.trim()) {
      setFormData({
        ...formData,
        skills: [...formData.skills, newSkillText.trim()],
      });
      setNewSkillText("");
    }
  };

  const handleDeleteSkill = (index) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((_, i) => i !== index),
    });
  };

  const handleEditSkill = (index) => {
    setEditingSkillIndex(index);
    setNewSkillText(formData.skills[index]);
  };

  const handleSaveEditSkill = () => {
    if (newSkillText.trim() && editingSkillIndex !== null) {
      const newSkills = [...formData.skills];
      newSkills[editingSkillIndex] = newSkillText.trim();
      setFormData({ ...formData, skills: newSkills });
      setNewSkillText("");
      setEditingSkillIndex(null);
    }
  };

  const handleCancelEditSkill = () => {
    setNewSkillText("");
    setEditingSkillIndex(null);
  };

  // Key Learnings handlers
  const handleAddKeyLearning = () => {
    if (newKeyLearningText.trim()) {
      setFormData({
        ...formData,
        keyLearnings: [...formData.keyLearnings, newKeyLearningText.trim()],
      });
      setNewKeyLearningText("");
    }
  };

  const handleDeleteKeyLearning = (index) => {
    setFormData({
      ...formData,
      keyLearnings: formData.keyLearnings.filter((_, i) => i !== index),
    });
  };

  const handleEditKeyLearning = (index) => {
    setEditingLearningIndex(index);
    setNewKeyLearningText(formData.keyLearnings[index]);
  };

  const handleSaveEditKeyLearning = () => {
    if (newKeyLearningText.trim() && editingLearningIndex !== null) {
      const newLearnings = [...formData.keyLearnings];
      newLearnings[editingLearningIndex] = newKeyLearningText.trim();
      setFormData({ ...formData, keyLearnings: newLearnings });
      setNewKeyLearningText("");
      setEditingLearningIndex(null);
    }
  };

  const handleCancelEditKeyLearning = () => {
    setNewKeyLearningText("");
    setEditingLearningIndex(null);
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "auto",
              width: "90%",
            }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
            >
              {projectData?.id ? "Edit Project" : "Add New Project"}
            </motion.h3>

            <div className="preview-modal-container">
              {/* Left Side: Project Image Preview */}

              <motion.div
                style={{ flex: 1 }}
                variants={childVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Title */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Project Title *
                  </label>
                  <input
                    className="glass__card"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Portfolio Website"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 0.5rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.8rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                    }}
                  />
                  {errors.title && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        position: "absolute",
                        bottom: "-1.25rem",
                      }}
                    >
                      {errors.title}
                    </span>
                  )}
                </motion.div>

                {/* Description */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Description *
                  </label>
                  <textarea
                    className="glass__card"
                    name="projectDescription"
                    value={formData.projectDescription}
                    onChange={handleInputChange}
                    placeholder="Brief description of the project..."
                    style={{
                      width: "100%",
                      minHeight: "95px",
                      marginTop: "0.5rem",
                      padding: "0.75rem 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                  />
                  {errors.projectDescription && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        position: "absolute",
                        bottom: "-1.25rem",
                      }}
                    >
                      {errors.projectDescription}
                    </span>
                  )}
                </motion.div>

                {/* Category */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    position: "relative",
                    marginBottom: "1rem",
                  }}
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Category *
                  </label>
                  <select
                    className="glass__card"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      cursor: "pointer",
                      appearance: "none",
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 0.75rem center",
                      backgroundSize: "1.5em 1.5em",
                      paddingRight: "2.5rem",
                    }}
                  >
                    <option value="">Select a category</option>
                    <option value="Web">Web</option>
                    <option value="app">App</option>
                    <option value="design">Design</option>
                  </select>
                  {errors.category && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        position: "absolute",
                        bottom: "-1.25rem",
                      }}
                    >
                      {errors.category}
                    </span>
                  )}
                </motion.div>
              </motion.div>
              {/* Right Side: Details */}
              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                className="image-upload-section"
                style={{
                  flex: "0.5 1 0%",
                  marginTop: "1.5rem",
                  gap: "1rem",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <motion.label
                  className="glass__card"
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    width: "100%",
                    aspectRatio: "4.39 / 3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                    cursor: "pointer",
                    position: "relative",
                    padding: "0.5rem",
                    border: "2px solid var(--title-color)",
                  }}
                >
                  {formData.image ? (
                    <>
                      <img
                        src={resolveImg(formData.image, 400)}
                        alt="Project Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "0.5rem",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          width: "2.5rem",
                          height: "2.5rem",
                          borderRadius: "0.375rem",
                          backgroundColor: "var(--title-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        <i
                          className="bx bx-upload"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--container-color)",
                          }}
                        ></i>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-color)",
                        textAlign: "center",
                        padding: "1rem",
                      }}
                    >
                      <i
                        className="bx bx-upload"
                        style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
                      ></i>
                      <span style={{ fontSize: "0.75rem" }}>
                        Upload Project Image
                      </span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </motion.label>

                {/* Project Image URL */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    position: "relative",
                  }}
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                      marginTop: "1rem",
                    }}
                  >
                    Image URL
                  </label>
                  <input
                    className="glass__card"
                    type="text"
                    value={formData.image}
                    onChange={(e) =>
                      setFormData({ ...formData, image: e.target.value })
                    }
                    placeholder="e.g., https://example.com/image.jpg"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                    }}
                    onClick={() =>
                      formData.image &&
                      navigator.clipboard.writeText(formData.image)
                    }
                    title={
                      formData.image ? "Click to copy URL" : "Paste URL here"
                    }
                  />
                </motion.div>
              </motion.div>
            </div>

            {/* Skills Section */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ marginBottom: "0.5rem" }}
            >
              <label
                className="preview-modal-subtitle"
                style={{
                  display: "block",
                  fontWeight: "600",
                  fontSize: "small",
                }}
              >
                Skills
              </label>
              {formData.skills.length >= 0 && (
                <>
                  {formData.skills.length > 0 && (
                    <ul
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        padding: 0,
                        marginTop: "0.5rem",
                        marginBottom: "0.5rem",
                        listStyle: "none",
                      }}
                    >
                      {formData.skills.map((skill, index) => (
                        <motion.div
                          className="glass__card skills__chip"
                          key={index}
                          variants={childVariants}
                          initial="hidden"
                          animate="visible"
                          style={{
                            border: "2px solid var(--title-color)",
                            borderRadius: "2rem",
                            padding: "0.5rem 1rem",
                            height: "2.5rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem",
                            position: "relative",
                            backgroundColor:
                              "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              color: "var(--title-color)",
                              margin: 0,
                              wordBreak: "break-word",
                            }}
                          >
                            {skill}
                          </p>
                          <div
                            style={{
                              top: "0.35rem",
                              right: "0.35rem",
                              display: "flex",
                              gap: "0.25rem",
                            }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditSkill(index)}
                              className="glass__button"
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "var(--body-color)",
                                padding: 0,
                                background: "var(--title-color)",
                              }}
                              title="Edit skill"
                            >
                              <i
                                className="bx bx-pencil"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteSkill(index)}
                              className="glass__button"
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "var(--body-color)",
                                padding: 0,
                                background: "var(--title-color)",
                              }}
                              title="Delete skill"
                            >
                              <i
                                className="bx bx-trash"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </motion.button>
                          </div>
                        </motion.div>
                      ))}

                      {/* Add New Skill Card - Hide when form is open */}
                      {!isAddingSkill && editingSkillIndex === null && (
                        <motion.div
                          className="glass__card"
                          variants={childVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsAddingSkill(true)}
                          style={{
                            border: "var(--border-color)",
                            borderRadius: "2rem",
                            padding: "0.5rem 1rem",
                            height: "2.5rem",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <i
                            className="bx bx-plus"
                            style={{
                              fontSize: "1.5rem",
                              color: "var(--title-color)",
                              marginBottom: "0.15rem",
                            }}
                          ></i>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: "500",
                              color: "var(--title-color)",
                            }}
                          >
                            Add Skill
                          </span>
                        </motion.div>
                      )}
                    </ul>
                  )}

                  {/* Add New Skill Card - Show when no skills exist */}
                  {formData.skills.length === 0 &&
                    !isAddingSkill &&
                    editingSkillIndex === null && (
                      <motion.div
                        className="glass__card"
                        variants={childVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsAddingSkill(true)}
                        style={{
                          border: "var(--border-color)",
                          borderRadius: "2rem",
                          padding: "0.5rem 1rem",
                          height: "2.5rem",
                          display: "flex",
                          width: "fit-content",
                          marginTop: "0.5rem",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="bx bx-plus"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--title-color)",
                            marginBottom: "0.15rem",
                          }}
                        ></i>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "500",
                            color: "var(--title-color)",
                          }}
                        >
                          Add Skill
                        </span>
                      </motion.div>
                    )}
                </>
              )}

              {/* Edit/Add Form Below All Cards */}
              {(isAddingSkill || editingSkillIndex !== null) && (
                <motion.div
                  variants={childVariantsDown}
                  initial="hidden"
                  animate="visible"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      fontSize: "small",
                      marginTop: "1rem",
                    }}
                  >
                    {editingSkillIndex !== null
                      ? "Edit Skill"
                      : "Add New Skill"}
                  </label>
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <input
                        type="text"
                        className="glass__card"
                        value={newSkillText}
                        onChange={(e) => setNewSkillText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            editingSkillIndex !== null
                              ? handleSaveEditSkill()
                              : handleAddSkill();
                          }
                        }}
                        placeholder="e.g., React, Node.js"
                        style={{
                          flex: 1,
                          padding: "0.5rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                        }}
                      />
                      {editingSkillIndex !== null ? (
                        <>
                          <ButtonComponent
                            buttonTitle="Save"
                            buttonText="Save"
                            onClick={handleSaveEditSkill}
                          />
                          <ButtonComponent
                            buttonTitle="Cancel"
                            buttonText="Cancel"
                            onClick={handleCancelEditSkill}
                          />
                        </>
                      ) : (
                        <>
                          <ButtonComponent
                            buttonTitle="Add"
                            buttonText="Add"
                            onClick={handleAddSkill}
                            disabled={!newSkillText.trim()}
                          />
                          <ButtonComponent
                            buttonTitle="Cancel"
                            buttonText="Cancel"
                            onClick={() => {
                              setIsAddingSkill(false);
                              setNewSkillText("");
                            }}
                          />
                        </>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>

            {/* Key Learnings Section */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ marginBottom: "0.5rem" }}
            >
              <label
                className="preview-modal-subtitle"
                style={{
                  display: "block",
                  fontWeight: "600",
                  fontSize: "small",
                }}
              >
                Key Learnings
              </label>
              {formData.keyLearnings.length >= 0 && (
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {formData.keyLearnings.length > 0 && (
                    <labl
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(130px, 1fr))",
                        gap: "0.5rem",
                        marginBottom: "1rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {formData.keyLearnings.map((learning, index) => (
                        <motion.div
                          className="glass__card"
                          variants={childVariants}
                          initial="hidden"
                          animate="visible"
                          key={index}
                          style={{
                            border: "2px solid var(--title-color)",
                            borderRadius: "0.5rem",
                            padding: "0.65rem",
                            position: "relative",
                            backgroundColor:
                              "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "0.35rem",
                              right: "0.35rem",
                              display: "flex",
                              gap: "0.25rem",
                            }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditKeyLearning(index)}
                              className="glass__button"
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "var(--body-color)",
                                padding: 0,
                                background: "var(--title-color)",
                              }}
                              title="Edit learning"
                            >
                              <i
                                className="bx bx-pencil"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteKeyLearning(index)}
                              className="glass__button"
                              style={{
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "var(--body-color)",
                                padding: 0,
                                background: "var(--title-color)",
                              }}
                              title="Delete learning"
                            >
                              <i
                                className="bx bx-trash"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </motion.button>
                          </div>
                          <p
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              color: "var(--title-color)",
                              margin: 0,
                              paddingRight: "2.5rem",
                              wordBreak: "break-word",
                            }}
                          >
                            {learning}
                          </p>
                        </motion.div>
                      ))}

                      {/* Add New Key Learning Card - Hide when form is open */}
                      {!isAddingKeyLearning &&
                        editingLearningIndex === null && (
                          <motion.div
                            className="glass__card"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setIsAddingKeyLearning(true)}
                            style={{
                              border: "var(--border-color)",
                              borderRadius: "0.5rem",
                              padding: "0.65rem",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: "65px",
                              cursor: "pointer",
                            }}
                          >
                            <i
                              className="bx bx-plus"
                              style={{
                                fontSize: "1.5rem",
                                color: "var(--title-color)",
                                marginBottom: "0.15rem",
                              }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: "500",
                                color: "var(--title-color)",
                              }}
                            >
                              Add Learning
                            </span>
                          </motion.div>
                        )}
                    </labl>
                  )}

                  {/* Add New Key Learning Card - Show when no learnings exist */}
                  {formData.keyLearnings.length === 0 &&
                    !isAddingKeyLearning &&
                    editingLearningIndex === null && (
                      <motion.div
                        className="glass__card"
                        variants={childVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsAddingKeyLearning(true)}
                        style={{
                          border: "var(--border-color)",
                          borderRadius: "2rem",
                          padding: "0.5rem 1rem",
                          height: "2.5rem",
                          display: "flex",
                          width: "fit-content",
                          marginTop: "0.5rem",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="bx bx-plus"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--title-color)",
                            marginBottom: "0.15rem",
                          }}
                        ></i>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "500",
                            color: "var(--title-color)",
                          }}
                        >
                          Add Learning
                        </span>
                      </motion.div>
                    )}
                </motion.div>
              )}

              {/* Edit/Add Form Below All Cards */}
              {(isAddingKeyLearning || editingLearningIndex !== null) && (
                <motion.div
                  variants={childVariantsDown}
                  initial="hidden"
                  animate="visible"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      fontSize: "small",
                      marginTop: "1rem",
                    }}
                  >
                    {editingLearningIndex !== null
                      ? "Edit Key Learning"
                      : "Add New Key Learning"}
                  </label>
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <input
                        className="glass__card"
                        type="text"
                        value={newKeyLearningText}
                        onChange={(e) => setNewKeyLearningText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            editingLearningIndex !== null
                              ? handleSaveEditKeyLearning()
                              : handleAddKeyLearning();
                          }
                        }}
                        placeholder="e.g., API Integration"
                        style={{
                          flex: 1,
                          padding: "0.5rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                        }}
                      />
                      {editingLearningIndex !== null ? (
                        <>
                          <ButtonComponent
                            buttonTitle="Save"
                            buttonText="Save"
                            onClick={handleSaveEditKeyLearning}
                          />
                          <ButtonComponent
                            buttonTitle="Cancel"
                            buttonText="Cancel"
                            onClick={handleCancelEditKeyLearning}
                          />
                        </>
                      ) : (
                        <>
                          <ButtonComponent
                            buttonTitle="Add"
                            buttonText="Add"
                            onClick={handleAddKeyLearning}
                            disabled={!newKeyLearningText.trim()}
                          />
                          <ButtonComponent
                            buttonTitle="Cancel"
                            buttonText="Cancel"
                            onClick={() => {
                              setIsAddingKeyLearning(false);
                              setNewKeyLearningText("");
                            }}
                          />
                        </>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>

            {/* Demo Link */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ position: "relative", marginBottom: "0.5rem" }}
            >
              <label
                className="preview-modal-subtitle"
                style={{
                  fontWeight: "600",
                  fontSize: "small",
                  marginTop: "1rem",
                }}
              >
                Demo Link
              </label>
              <input
                className="glass__card"
                type="url"
                name="demolink"
                value={formData.demolink}
                onChange={handleInputChange}
                placeholder="e.g., https://project-demo.com"
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",

                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                }}
              />
            </motion.div>

            {/* Code Link */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ position: "relative", marginBottom: "1.5rem" }}
            >
              <label
                className="preview-modal-subtitle"
                style={{
                  fontWeight: "600",
                  fontSize: "small",
                  marginTop: "1rem",
                }}
              >
                Code Link
              </label>
              <input
                className="glass__card"
                type="url"
                name="codelink"
                value={formData.codelink}
                onChange={handleInputChange}
                placeholder="e.g., https://github.com/username/repo"
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",
                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                }}
              />
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <ButtonComponent
                buttonTitle="Cancel"
                buttonText="Cancel"
                onClick={onClose}
              />
              <ButtonComponent
                buttonTitle={isNewProject ? "Add Project" : "Save Changes"}
                buttonText={
                  uploading
                    ? "Uploading..."
                    : isNewProject
                      ? "Add Project"
                      : "Save Changes"
                }
                onClick={handleSave}
                disabled={uploading}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditProjectModal;
